import { getErrorMessage } from '../utils/errorMessage';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Gift, Loader2, Mail, Moon, ShieldCheck, Sun, UserRound } from 'lucide-react';
import api from '../utils/api';
import useTheme from '../hooks/useTheme';
import AnimatedBrandText from '../vnext/home/AnimatedBrandText';
import XiaoyingMark from '../vnext/components/XiaoyingMark';
import '../vnext/components/XiaoyingMark.css';
import '../vnext/auth/LoginPage.css';
import { sendEmailRegistrationCode, verifyEmailRegistrationCode } from '../vnext/auth/wechatAuthApi';
import { safeReturnTarget } from '../vnext/auth/authSession';
import PublicTeamBrandProvider from '../vnext/branding/PublicTeamBrandProvider';
import { BrandText } from '../vnext/branding/TeamBrandContext';
import { teamLoginContext } from '../vnext/auth/teamLogin';

export default function Register() {
  const [form, setForm] = useState({ username: '', email: '', password: '', ref: '' });
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, setTheme } = useTheme();
  const teamContext = teamLoginContext(location.search);
  const hasTeamTarget = Object.hasOwn(teamContext, 'login_team_id');
  const returnTarget = hasTeamTarget ? '/home' : safeReturnTarget(location.state?.from);
  const normalizedEmail = form.email.trim();
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail);
  const darkActive = theme === 'dark' || (theme === 'system' && window.matchMedia?.('(prefers-color-scheme: dark)')?.matches);

  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get('ref');
    if (ref) setForm(current => ({ ...current, ref: ref.toUpperCase() }));
  }, []);

  useEffect(() => {
    if (countdown <= 0) return undefined;
    const timer = window.setTimeout(() => setCountdown(value => Math.max(0, value - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [countdown]);

  const sendCode = async () => {
    if (!emailValid || sendingCode || countdown > 0) return;
    setSendingCode(true);
    setError('');
    setNotice('');
    try {
      const result = await sendEmailRegistrationCode(normalizedEmail);
      setCountdown(Number(result?.resend_after_seconds) || 60);
      setNotice(`验证码已发送至 ${result?.email || normalizedEmail}`);
    } catch (requestError) {
      setError(getErrorMessage(requestError, '验证码邮件发送失败，请稍后再试'));
    } finally {
      setSendingCode(false);
    }
  };

  const submit = async event => {
    event.preventDefault();
    if (!form.username.trim()) { setError('请输入用户名'); return; }
    if (!emailValid) { setError('请输入正确的邮箱地址'); return; }
    if (!/^\d{6}$/.test(emailCode)) { setError('请输入邮件中的六位验证码'); return; }
    if (form.password.length < 8) { setError('密码至少需要 8 位'); return; }
    setLoading(true);
    setError('');
    setNotice('');
    try {
      const verification = await verifyEmailRegistrationCode(normalizedEmail, emailCode);
      const response = await api.post('/auth/register', { ...form, ...teamContext, email: normalizedEmail, email_verification_token: verification.registration_token });
      localStorage.setItem('token', response.data.token);
      window.dispatchEvent(new CustomEvent('auth-changed', { detail: { user: response.data.user } }));
      navigate(returnTarget, { replace: true });
    } catch (requestError) {
      setError(getErrorMessage(requestError, '注册失败，请重试'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicTeamBrandProvider>
    <main className="vnext-auth vnext-register relative flex min-h-screen items-center justify-center overflow-x-hidden overflow-y-auto px-4 py-8 sm:px-8">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true"><div className="vnext-auth__glow vnext-auth__glow--lavender absolute left-[8%] top-[14%] h-72 w-72 rounded-full blur-[120px]" /><div className="vnext-auth__glow vnext-auth__glow--sage absolute bottom-[4%] right-[8%] h-80 w-80 rounded-full blur-[140px]" /></div>

      <Link to={`/login${location.search || ''}`} state={{ from: returnTarget }} className="vnext-auth__floating-control absolute left-5 top-5 z-20 inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-[12px] backdrop-blur-xl transition sm:left-8 sm:top-8"><ArrowLeft size={14} />返回登录</Link>
      <button type="button" onClick={() => setTheme(darkActive ? 'light' : 'dark')} className="vnext-auth__floating-control absolute right-5 top-5 z-20 flex h-9 w-9 items-center justify-center rounded-full backdrop-blur-xl transition sm:right-8 sm:top-8" aria-label={darkActive ? '切换到浅色模式' : '切换到深色模式'}>{darkActive ? <Sun size={15} /> : <Moon size={15} />}</button>

      <section className="vnext-auth__card vnext-register__card relative my-auto grid w-full max-w-[1040px] overflow-hidden rounded-[30px] lg:grid-cols-[0.92fr_1.08fr]">
        <div className="flex min-h-[650px] flex-col px-7 py-8 sm:px-12 sm:py-11 lg:min-h-[680px]">
          <header className="flex items-center gap-2.5"><XiaoyingMark size={34} /><h2 className="text-[23px] font-semibold tracking-[-0.03em]"><AnimatedBrandText /><span className="sr-only"><BrandText>小影</BrandText></span></h2></header>
          <div className="mt-9"><p className="vnext-auth__faint text-[10px] font-semibold uppercase tracking-[0.2em]">Create your studio</p><h1 className="mt-3 text-[28px] font-semibold tracking-[-0.04em]"><BrandText>创建你的小影账号</BrandText></h1><p className="vnext-auth__muted mt-2 text-[11px] leading-5">验证邮箱后，项目、画布和资产都会安全同步。</p></div>

          <form onSubmit={submit} noValidate className="vnext-register__form mt-6 flex flex-1 flex-col">
            {hasTeamTarget && <p className="vnext-auth__muted mb-4 text-[11px] leading-5" role="status">注册后直接加入此团队，不会另建默认团队。</p>}
            <div className="vnext-register__grid">
              <label><span>用户名</span><div className="vnext-auth__field"><UserRound size={14} /><input autoComplete="nickname" value={form.username} onChange={event => setForm({ ...form, username: event.target.value })} placeholder="以后在团队里显示的名字" /></div></label>
              <label><span>推荐码 <em>选填</em></span><div className="vnext-auth__field"><Gift size={14} /><input value={form.ref} onChange={event => setForm({ ...form, ref: event.target.value.toUpperCase() })} maxLength={10} placeholder="有推荐码可以填在这里" /></div></label>
            </div>

            <label className="mt-4"><span>注册邮箱</span><div className="vnext-auth__field"><Mail size={14} /><input type="email" autoComplete="username" value={form.email} onChange={event => { setForm({ ...form, email: event.target.value }); setEmailCode(''); setNotice(''); }} placeholder="you@example.com" /></div></label>

            <div className="vnext-register__verify mt-4">
              <label><span>邮箱验证码</span><div className="vnext-auth__field"><ShieldCheck size={14} /><input inputMode="numeric" autoComplete="one-time-code" value={emailCode} onChange={event => setEmailCode(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="六位验证码" /></div></label>
              <button type="button" onClick={sendCode} disabled={!emailValid || sendingCode || countdown > 0}>{sendingCode ? <><Loader2 size={13} className="animate-spin" />发送中</> : countdown > 0 ? `${countdown}s 后重发` : '获取验证码'}</button>
            </div>

            <label className="mt-4"><span>设置密码</span><div className="vnext-auth__field"><ShieldCheck size={14} /><input type="password" autoComplete="new-password" value={form.password} onChange={event => setForm({ ...form, password: event.target.value })} minLength={8} maxLength={64} placeholder="至少 8 个字符" /></div></label>

            {error && <p className="vnext-auth__error mt-4 text-[11px] leading-5" role="alert">{error}</p>}
            {notice && <p className="vnext-register__notice mt-4" role="status"><ShieldCheck size={13} />{notice}</p>}

            <div className="mt-auto pt-5">
              <button type="submit" disabled={loading} className="vnext-auth__submit flex h-11 w-full items-center justify-center gap-2 rounded-[11px] text-[12px] font-semibold transition disabled:cursor-not-allowed">{loading ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}{loading ? '正在创建账号…' : '验证并创建账号'}</button>
              <p className="vnext-register__login">已经有账号？ <Link to={`/login${location.search || ''}`} state={{ from: returnTarget }}>直接登录</Link></p>
            </div>
          </form>
        </div>

        <aside className="vnext-auth__artwork relative hidden min-h-[680px] overflow-hidden lg:block" aria-label="原创创作视觉"><img src="/images/vnext/writing.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" /><div className="absolute inset-0 bg-gradient-to-t from-[#111014]/92 via-[#111014]/16 to-[#111014]/22" /><div className="absolute inset-x-0 bottom-0 p-10"><p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/44">Start with one idea</p><p className="mt-4 max-w-md text-[25px] font-medium leading-[1.35] tracking-[-0.035em] text-white/92">一个账号，<br />收好每一次创作。</p><p className="mt-4 max-w-sm text-[12px] leading-6 text-white/48">注册完成后即可继续使用写作台、无限画布、模板和资产中心。</p></div></aside>
      </section>
    </main>
    </PublicTeamBrandProvider>
  );
}
